import { HL_API_URL, HL_PUBLISHABLE_KEY } from "../../config/env";

// The 450labs shop gateway. Products, prices and orders live in the 450labs panel;
// the site only reads the catalog and sends ids + quantities. Amounts are integer cents.

export type PaymentMethod = "card" | "cod";

export interface ShopConfig {
  currency: string;
  payment_methods: PaymentMethod[];
  delivery: { fee_cents: number; free_above_cents: number | null };
  legal: { terms: string | null; privacy: string | null; returns: string | null };
}

export interface ProductVariant {
  id: string;
  selector: string | Record<string, string>;
  price_delta_cents: number;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  images: Array<string | { url: string }>;
  variants: ProductVariant[];
  available: boolean;
  metafields: Record<string, unknown>;
}

export interface CheckoutItem {
  product_id: string;
  variant_id?: string;
  quantity: number;
}

export interface CheckoutCustomer {
  name: string;
  email: string;
  phone: string;
  address?: Record<string, string>;
}

export interface CheckoutBody {
  items: CheckoutItem[];
  customer: CheckoutCustomer;
  payment_method: PaymentMethod;
  promo_code?: string;
  privacy_accepted: true;
  success_url?: string;
  cancel_url?: string;
}

export interface CheckoutResult {
  order_id: string;
  order_token: string;
  checkout_url?: string;
  status?: string;
}

export interface OrderStatus {
  order_id: string;
  status: string;
  paid: boolean;
  total_cents: number;
  currency: string;
  is_test: boolean;
}

export class ShopError extends Error {
  constructor(
    public code: string,
    public status: number,
    public detail?: string,
  ) {
    super(code);
    this.name = "ShopError";
  }
}

export const isShopConnected = Boolean(HL_PUBLISHABLE_KEY);

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  if (!HL_PUBLISHABLE_KEY) throw new ShopError("missing_key", 0);
  let res: Response;
  try {
    res = await fetch(`${HL_API_URL}${path}`, {
      method: init.method ?? "GET",
      headers: {
        Authorization: `Bearer ${HL_PUBLISHABLE_KEY}`,
        ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ShopError("network", 0);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ShopError(body?.error || `http_${res.status}`, res.status, body?.message);
  return body as T;
}

export const shopApi = {
  getConfig: () => call<ShopConfig>("/shop/config"),
  listProducts: () => call<{ products: Product[] }>("/products").then((r) => r.products),
  getProduct: (id: string) =>
    call<{ product: Product }>(`/products/${encodeURIComponent(id)}`).then((r) => r.product),
  // Never retried: a second POST could place a second order.
  checkout: (body: CheckoutBody) => call<CheckoutResult>("/checkout", { method: "POST", body }),
  getOrder: (orderId: string, token: string) =>
    call<OrderStatus>(`/orders/${encodeURIComponent(orderId)}?token=${encodeURIComponent(token)}`),
  // A return nonce can be traded once; a second trade answers 404 return_not_found.
  tradeReturn: (nonce: string) =>
    call<{ order_id: string; order_token: string }>(`/orders/by-return/${encodeURIComponent(nonce)}`),
  verifyPayment: (orderId: string, token: string) =>
    call<{ paid: boolean; status: string }>(`/orders/${encodeURIComponent(orderId)}/verify-payment`, {
      method: "POST",
      body: { token },
    }),
};

const MESSAGES: Record<string, string> = {
  network: "Couldn't reach the store. Check your connection and try again.",
  missing_key: "The store isn't connected right now.",
  invalid_key: "The store isn't connected right now.",
  revoked_key: "The store isn't connected right now.",
  origin_not_allowed: "The store doesn't accept orders from this address.",
  product_not_live: "The store is closed right now.",
  product_suspended: "The store is closed right now.",
  consent_required: "Tick the box to agree to the terms and the privacy policy first.",
  invalid_request: "Something in the form isn't right. Check the fields and try again.",
  invalid_promo_code: "That promo code doesn't work.",
  product_not_available: "One of the items isn't available any more.",
  variant_not_available: "That size isn't available any more.",
  order_intake_paused: "The store isn't taking orders right now. Try again later.",
  checkout_session_failed: "The payment page didn't open. Try again in a minute.",
  card_test_unavailable: "Card test orders aren't set up for this store.",
  shop_not_configured: "The store is having trouble right now. Try again in a bit.",
  shop_unavailable: "The store is having trouble right now. Try again in a bit.",
  invalid_order_token: "We couldn't find that order.",
  return_not_found: "We couldn't find that order.",
  url_not_allowed: "Checkout can't start from this page.",
  success_url_required: "Checkout can't start from this page.",
  cancel_url_required: "Checkout can't start from this page.",
};

export function shopErrorMessage(err: unknown): string {
  const code = err instanceof ShopError ? err.code : "";
  return MESSAGES[code] ?? "Something went wrong. Try again in a bit.";
}

/** €4 for whole amounts, €4.50 otherwise, like the old store cards. */
export function formatCents(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function productImageUrls(product: Product): string[] {
  return (product.images ?? [])
    .map((img) => (typeof img === "string" ? img : img?.url))
    .filter((url): url is string => Boolean(url));
}

export function variantLabel(variant: ProductVariant): string {
  if (typeof variant.selector === "string") return variant.selector;
  return Object.values(variant.selector ?? {}).join(" / ");
}
